import React, { memo, useCallback, useMemo } from 'react'

import { FiltersSidebar } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import { FILTER_NAME } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import {
  addAdvancedFilter,
  removeAdvancedFilter,
  setPersonalFilter,
  setPurchaseFilter,
} from 'Pods/SearchPage/Store/actions/moreFilters'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import {
  getAdvancedFiltersConfig,
  getPersonalFiltersConfig,
  getPurchaseFiltersConfig,
} from './constants'

// hideAdvancedLabel drops only the first group's "ADVANCED" title when an external title already
// names the section (mobile collapsible header). The other group labels stay as sub-headings.
function AdvancedFiltersContent({ hideAdvancedLabel = false }) {
  const translate = useTranslate()

  const advancedFilters = useSearchPageStore(state => state.advancedFilters ?? EMPTY_ARRAY)
  const personalFilter = useSearchPageStore(state => state.personalFilter)
  const purchaseFilter = useSearchPageStore(state => state.purchaseFilter)
  // Personal catalog filters (wishlist, purchased) are backed by ItemsSearch filters that no-op
  // without current_user, so a signed-out viewer would see an "active" filter over unfiltered
  // results. Hidden until pageConfig confirms a logged-in user (client-side; SSR is user-blind).
  const pageConfig = usePageConfig()
  const isLoggedIn = !!pageConfig?.currentUserId
  const trackFilterSelect = useTrackFilterSelect()

  const handleAdvancedFilterChange = useCallback((_, value, isOn) => {
    if (isOn) {
      trackFilterSelect(FILTER_NAME.ADVANCED, value)
      addAdvancedFilter(value)
    } else {
      removeAdvancedFilter(value)
    }
  }, [trackFilterSelect])

  const handlePersonalFilterChange = useCallback((_, value) => {
    trackFilterSelect(FILTER_NAME.PERSONAL, value)
    setPersonalFilter(value)
  }, [trackFilterSelect])

  const handlePurchaseFilterChange = useCallback((_, value) => {
    trackFilterSelect(FILTER_NAME.PURCHASES, value)
    setPurchaseFilter(value)
  }, [trackFilterSelect])

  const selectedPersonalFilter = useMemo(() => {
    return personalFilter != null ? [personalFilter] : []
  }, [personalFilter])

  const selectedPurchaseFilter = useMemo(() => {
    return purchaseFilter != null ? [purchaseFilter] : []
  }, [purchaseFilter])

  const advancedConfig = useMemo(() => {
    const config = getAdvancedFiltersConfig(translate)
    return hideAdvancedLabel ? [{ ...config[0], label: '' }] : config
  }, [hideAdvancedLabel, translate])

  const personalConfig = useMemo(() => getPersonalFiltersConfig(translate), [translate])
  const purchaseConfig = useMemo(() => getPurchaseFiltersConfig(translate), [translate])

  return (
    <>
      <FiltersSidebar
        isCollapsible={false}
        filters={advancedConfig}
        selectedFilters={advancedFilters}
        onFiltersChange={handleAdvancedFilterChange}
        isControlled
        useNewCheckboxes
        hideLastDivider={!isLoggedIn}
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
      />
      {isLoggedIn && (
        <>
          <FiltersSidebar
            isCollapsible={false}
            filters={personalConfig}
            selectedFilters={selectedPersonalFilter}
            onFiltersChange={handlePersonalFilterChange}
            hideShowMore
            baseFormWrapperClassName="filter-wrapper__content"
            useRadio
          />
          <FiltersSidebar
            isCollapsible={false}
            filters={purchaseConfig}
            selectedFilters={selectedPurchaseFilter}
            onFiltersChange={handlePurchaseFilterChange}
            hideLastDivider
            hideShowMore
            baseFormWrapperClassName="filter-wrapper__content"
            useRadio
          />
        </>
      )}
    </>
  )
}

AdvancedFiltersContent.propTypes = {
  hideAdvancedLabel: bool,
}

export default memo(AdvancedFiltersContent)
